import { useLayoutEffect, useRef, useState } from "react";

import { usePostHog } from "posthog-js/react";

import { ClientAppEvent } from "constants/index";
import {
  useCarrierShipmentPageContext,
  useRoleContext,
  useWriterComponentState,
} from "hooks";

import { NoCapacityButton } from "./NoCapacityButton";

const NO_CAPACITY_ERROR_MESSAGE =
  "Failed to mark no truck available. Please try again.";
const UNDO_NO_CAPACITY_ERROR_MESSAGE =
  "Failed to undo no truck available. Please try again.";

export function NoCapacityButtonContainer() {
  const { shipment, onRefresh } = useCarrierShipmentPageContext();
  const { company: carrierCompany } = useRoleContext();
  const posthog = usePostHog();

  const shipmentId = shipment?.info.id;
  const serverMarkedNoCapacity = shipment?.no_capacity_response != null;

  // A mark/undo request started on shipment A can resolve after the carrier
  // has already navigated to shipment B (this component stays mounted across
  // that switch). The analytics event and cache revalidation still belong to
  // A and are safe to fire regardless, but applying A's outcome to what is
  // now B's button state would not be. A ref (not the render-time state below)
  // is needed here: the success callbacks close over whatever was true at
  // click time, so only a value that is mutated and read live -- outside that
  // closure -- can tell a callback its own click has gone stale. Written in
  // useLayoutEffect rather than useEffect so it is never behind a passive
  // effect that a fast-resolving request could in principle race.
  const currentShipmentIdRef = useRef(shipmentId);
  useLayoutEffect(() => {
    currentShipmentIdRef.current = shipmentId;
  }, [shipmentId]);

  // onRefresh triggers SWR revalidation but does not wait for it, so the old
  // no_capacity_response can still be in the shipment payload for a moment
  // after a successful mark/undo. This override renders the outcome the
  // request just confirmed until the refetched shipment catches up, then
  // clears so the server value is the source of truth again. A failed
  // request leaves it untouched, so the action stays retryable.
  const [markedNoCapacityOverride, setMarkedNoCapacityOverride] = useState<
    boolean | null
  >(null);

  // Clearing the override once the server catches up is a render-time state
  // adjustment (React's recommended alternative to an effect for this), not a
  // side effect -- it needs to happen before this render commits, not after.
  // Tracked alongside shipmentId, not just the server flag: this component
  // stays mounted across a shipment switch via the quick-shipment-browser
  // sidebar link (same route, different :id), so a same-valued flag on the
  // next shipment would otherwise never look like a change.
  const [lastSeenShipmentState, setLastSeenShipmentState] = useState({
    shipmentId,
    serverMarkedNoCapacity,
  });
  if (
    shipmentId !== lastSeenShipmentState.shipmentId ||
    serverMarkedNoCapacity !== lastSeenShipmentState.serverMarkedNoCapacity
  ) {
    setLastSeenShipmentState({ shipmentId, serverMarkedNoCapacity });
    setMarkedNoCapacityOverride(null);
  }

  const {
    loading: markLoading,
    error: markError,
    write: writeMark,
  } = useWriterComponentState({
    writeConfig: {
      method: "post",
      url: `/shipments/${shipmentId}/no-capacity`,
    },
    errorMessage: NO_CAPACITY_ERROR_MESSAGE,
  });

  const {
    loading: undoLoading,
    error: undoError,
    write: writeUndo,
  } = useWriterComponentState({
    writeConfig: {
      method: "post",
      url: `/shipments/${shipmentId}/no-capacity/undo`,
    },
    errorMessage: UNDO_NO_CAPACITY_ERROR_MESSAGE,
  });

  // The writer hooks' loading/error state is not tied to a shipment, so a
  // request started on shipment A would otherwise show its spinner -- and, if
  // it fails after the switch, its error -- on shipment B's button.
  const [lastWriteShipmentId, setLastWriteShipmentId] = useState<
    number | undefined
  >(undefined);
  const isLastWriteForCurrentShipment = lastWriteShipmentId === shipmentId;

  if (
    shipment == null ||
    shipment.has_carrier_active_quote ||
    shipment.shipment_status !== "ACTIVE"
  ) {
    return null;
  }

  const onMark = () => {
    const clickedShipmentId = shipmentId;
    const clickedShipperCompanyId = shipment.shipment_company.id;
    setLastWriteShipmentId(clickedShipmentId);
    writeMark(() => {
      posthog?.capture(ClientAppEvent.NO_CAPACITY_CLICKED, {
        source: "shipment_page",
        shipment_id: clickedShipmentId,
        shipper_company_id: clickedShipperCompanyId,
        carrier_company_id: carrierCompany?.id,
      });
      onRefresh?.();
      if (currentShipmentIdRef.current === clickedShipmentId) {
        setMarkedNoCapacityOverride(true);
      }
    });
  };

  const onUndo = () => {
    const clickedShipmentId = shipmentId;
    const clickedShipperCompanyId = shipment.shipment_company.id;
    setLastWriteShipmentId(clickedShipmentId);
    writeUndo(() => {
      posthog?.capture(ClientAppEvent.NO_CAPACITY_UNDONE, {
        source: "shipment_page",
        shipment_id: clickedShipmentId,
        shipper_company_id: clickedShipperCompanyId,
        carrier_company_id: carrierCompany?.id,
      });
      onRefresh?.();
      if (currentShipmentIdRef.current === clickedShipmentId) {
        setMarkedNoCapacityOverride(false);
      }
    });
  };

  return (
    <NoCapacityButton
      hasMarkedNoCapacity={markedNoCapacityOverride ?? serverMarkedNoCapacity}
      isLoading={isLastWriteForCurrentShipment && (markLoading || undoLoading)}
      error={isLastWriteForCurrentShipment ? (markError ?? undoError) : null}
      onMark={onMark}
      onUndo={onUndo}
    />
  );
}
